import { useState, useCallback, useEffect } from "react";
import {
  collection, getDocs, addDoc, deleteDoc, doc, query, orderBy,
} from "../services/firebase";

const firestoreErrorCode = (error) => (error?.code || "").replace(/^firestore\//, "");

export function useReviews(fbDb, fbAuth, firebaseUid) {
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 0, hoverRating: 0, title: "", content: "",
    scores: { food: 0, stay: 0, sight: 0, move: 0 }, photos: [],
  });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewsLoadError, setReviewsLoadError] = useState("");
  const [likedReviewIds, setLikedReviewIds] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("likedReviews") || "[]")); }
    catch (e) { return new Set(); }
  });

  const loadReviews = useCallback(async () => {
    if (!fbDb) {
      setReviewsLoadError("후기 목록을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.");
      setReviewsLoaded(true);
      return false;
    }

    setReviewsLoaded(false);
    setReviewsLoadError("");
    try {
      const reviewsQuery = query(collection(fbDb, "reviews"), orderBy("createdAt", "desc"));
      const snap = await getDocs(reviewsQuery);
      setReviews(snap.docs.map(reviewDoc => ({ id: reviewDoc.id, ...reviewDoc.data() })));
      return true;
    } catch (e) {
      const code = firestoreErrorCode(e);
      if (code === "permission-denied") {
        console.warn("[reviews-load] Firestore 권한 거부로 후기 목록을 비웁니다. 배포된 규칙을 확인해주세요.", e);
        setReviews([]);
        setReviewsLoadError("");
        return true;
      }
      console.error("리뷰 목록 로드 실패:", e);
      setReviewsLoadError(
        code === "unavailable"
          ? "후기 서비스에 일시적으로 연결할 수 없습니다. 잠시 후 다시 시도해주세요."
          : "후기 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
      );
      return false;
    } finally {
      setReviewsLoaded(true);
    }
  }, [fbDb]);

  useEffect(() => {
    loadReviews();
  }, [firebaseUid, loadReviews]);

  const submitReview = useCallback(async (travel, personal) => {
    const authorUid = firebaseUid || fbAuth?.currentUser?.uid;
    setReviewSuccess(false);
    setReviewError("");

    if (!fbDb) {
      setReviewError("후기를 저장할 수 없습니다. 잠시 후 다시 시도해주세요.");
      return false;
    }
    if (!authorUid) {
      setReviewError("후기를 등록하려면 먼저 로그인해주세요.");
      return false;
    }
    if (!travel?.destination || !personal?.name) {
      setReviewError("여행지와 작성자 정보를 확인한 뒤 다시 등록해주세요.");
      return false;
    }

    setReviewSubmitting(true);
    try {
      const review = {
        ...reviewForm,
        destination: travel.destination,
        startDate: travel.startDate,
        endDate: travel.endDate,
        author: personal.name,
        authorUid,
        createdAt: Date.now(),
      };
      const reviewDoc = await addDoc(collection(fbDb, "reviews"), review);
      setReviews(prev => [{ id: reviewDoc.id, ...review }, ...prev]);
      setReviewSuccess(true);
      setTimeout(() => setReviewSuccess(false), 2500);
      setReviewForm({ rating: 0, hoverRating: 0, title: "", content: "", scores: { food: 0, stay: 0, sight: 0, move: 0 }, photos: [] });
      return true;
    } catch (e) {
      console.error("리뷰 저장 실패:", e);
      setReviewError("후기 등록에 실패했습니다. 네트워크 연결을 확인한 뒤 다시 시도해주세요.");
      return false;
    } finally {
      setReviewSubmitting(false);
    }
  }, [fbAuth, fbDb, firebaseUid, reviewForm]);

  const deleteReview = useCallback(async (id) => {
    if (!fbDb) {
      setReviewError("후기를 삭제할 수 없습니다. 잠시 후 다시 시도해주세요.");
      return false;
    }
    setReviewError("");
    try {
      await deleteDoc(doc(fbDb, "reviews", id));
      setReviews(prev => prev.filter(r => r.id !== id));
      return true;
    } catch (e) {
      console.error("리뷰 삭제 실패:", e);
      setReviewError("후기 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.");
      return false;
    }
  }, [fbDb]);

  const toggleLike = useCallback((id) => {
    setLikedReviewIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { sessionStorage.setItem("likedReviews", JSON.stringify([...next])); } catch (e) {}
      return next;
    });
  }, []);

  return {
    reviews, setReviews, reviewsLoaded, setReviewsLoaded,
    reviewForm, setReviewForm, reviewSubmitting, setReviewSubmitting,
    editingReview, setEditingReview, reviewSuccess, setReviewSuccess,
    reviewError, setReviewError, reviewsLoadError, setReviewsLoadError,
    likedReviewIds, setLikedReviewIds,
    loadReviews, submitReview, deleteReview, toggleLike,
  };
}
